// Validação do fluxo do canvas — função pura, sem i18n (retorna chaves +
// params pro componente traduzir). Cada issue: { level: 'error'|'warn', key, params }.
// Não bloqueia salvar; só avisa o que pode deixar o agente quebrado/incompleto.
// Nós que fazem parte do CAMINHO do fluxo (precisam estar conectados).
// Ferramentas (tool_*, HTTP), notas e prospecção são standalone por natureza:
// o motor lê ferramenta por tipo, ligada ou não — não viram "órfãos".
const FLOW_KINDS = [
  'agent',
  'classify',
  'guardrails',
  'approve',
  'if_else',
  'send',
  'end',
];

// O motor usa só a CONFIGURAÇÃO do primeiro bloco desses tipos. Vários "Fim"
// (um por caminho) são normais — só avisa quando o extra traz configuração
// própria diferente da do primeiro, porque essa é ignorada calada.
const SINGLETON_CONFIG_KEYS = {
  send: ['responseFormat', 'fixedMessage'],
  end: [
    'closingMessage',
    'offerMore',
    'collectFeedback',
    'tags',
    'resolveOnEnd',
  ],
  guardrails: null, // sempre avisa: o 2º bloco inteiro é ignorado
};
const configSignature = (kind, d = {}) =>
  JSON.stringify(
    (SINGLETON_CONFIG_KEYS[kind] || []).map(k => {
      const v = d[k];
      return v === '' || v === false || v == null ? null : v;
    })
  );

// Saídas (ramos) de cada bloco de lógica — cada uma precisa de uma linha.
const branchesOf = d => {
  switch (d.kind) {
    case 'if_else':
      return [
        { id: 'true', label: d.trueLabel || 'Sim' },
        { id: 'false', label: d.falseLabel || 'Não' },
      ];
    case 'guardrails':
      return [
        { id: 'ok', label: 'ok' },
        { id: 'blocked', label: 'blocked' },
      ];
    case 'classify':
      return (d.categories || [])
        .filter(c => c && c.name)
        .map(c => ({ id: `cat:${c.name}`, label: c.name }));
    default:
      return [];
  }
};

export function validateFlow(nodes = [], edges = []) {
  const issues = [];
  const touched = new Set();
  edges.forEach(e => {
    touched.add(e.source);
    touched.add(e.target);
  });

  const isBlank = v => !String(v ?? '').trim();

  const agents = nodes.filter(n => n.data?.kind === 'agent');
  if (agents.length === 0) {
    issues.push({ level: 'error', key: 'NO_AGENT' });
  } else if (isBlank(agents[0].data?.instruction)) {
    issues.push({ level: 'warn', key: 'AGENT_NO_PROMPT' });
  }

  // Segundo Enviar/Fim/Proteções com configuração própria: o motor só lê a
  // do primeiro.
  Object.keys(SINGLETON_CONFIG_KEYS).forEach(kind => {
    const same = nodes.filter(n => n.data?.kind === kind);
    if (same.length < 2) return;
    const first = configSignature(kind, same[0].data);
    same.slice(1).forEach(n => {
      const always = SINGLETON_CONFIG_KEYS[kind] === null;
      const sig = configSignature(kind, n.data);
      const hasOwn = sig !== configSignature(kind, {}) && sig !== first;
      if (!always && !hasOwn) return;
      issues.push({
        level: 'warn',
        key: 'DUPLICATE_IGNORED',
        params: { label: n.data?.label || kind },
      });
    });
  });

  nodes.forEach(n => {
    const d = n.data || {};
    const label = d.label || d.kind;

    if (d.kind === 'start') {
      if (!touched.has(n.id))
        issues.push({ level: 'warn', key: 'START_DISCONNECTED' });
      return;
    }
    if (FLOW_KINDS.includes(d.kind) && !touched.has(n.id)) {
      issues.push({ level: 'warn', key: 'ORPHAN', params: { label } });
    }
    if (d.kind === 'http_request' && isBlank(d.endpoint)) {
      issues.push({
        level: 'error',
        key: 'HTTP_NO_ENDPOINT',
        params: { label },
      });
    }
    if (
      d.kind === 'http_request' &&
      d.enabled !== false &&
      /SEU-SISTEMA/i.test(d.endpoint || '')
    ) {
      issues.push({
        level: 'warn',
        key: 'HTTP_PLACEHOLDER',
        params: { label },
      });
    }
    // Agendamento ligado sem agenda escolhida: toda consulta volta
    // "nenhuma agenda configurada" e a IA escala o cliente que queria marcar.
    if (d.kind === 'tool_schedule_meeting' && d.enabled !== false) {
      const mode = d.calendarMode || (d.calendarUserId ? 'user' : 'pages');
      const semAgenda =
        (mode === 'pages' && !(d.schedulingPageIds || []).length) ||
        (mode === 'user' && !d.calendarUserId) ||
        (mode === 'round_robin' && !(d.calendarUserIds || []).length);
      if (semAgenda)
        issues.push({
          level: 'warn',
          key: 'MEETING_NO_CALENDAR',
          params: { label },
        });
    }
    if (d.kind === 'tool_deal' && d.enabled !== false) {
      if (!d.funnelId)
        issues.push({
          level: 'warn',
          key: 'DEAL_NO_FUNNEL',
          params: { label },
        });
      else if (['moveMode', 'lostMode', 'wonMode'].every(k => d[k] === 'off'))
        issues.push({ level: 'warn', key: 'DEAL_ALL_OFF', params: { label } });
    }
    if (
      d.kind === 'tool_update_contact' &&
      d.enabled !== false &&
      !(d.allowedFields || []).length
    ) {
      issues.push({
        level: 'warn',
        key: 'UPDATE_CONTACT_NO_FIELDS',
        params: { label },
      });
    }
    if (d.kind === 'classify') {
      const cats = (d.categories || []).filter(c => c && c.name);
      if (cats.length < 2)
        issues.push({ level: 'warn', key: 'CLASSIFY_FEW', params: { label } });
    }
    if (d.kind === 'approve' && isBlank(d.approvalPrompt)) {
      issues.push({
        level: 'warn',
        key: 'APPROVE_NO_PROMPT',
        params: { label },
      });
    }
    if (
      d.kind === 'if_else' &&
      isBlank(d.condition) &&
      !(d.conditionRules || []).length
    ) {
      issues.push({ level: 'warn', key: 'IFELSE_NO_COND', params: { label } });
    }
    // Ramo pendurado: bloco de lógica ligado ao fluxo, mas com uma saída sem
    // linha (o caminho "Não"/"blocked"/categoria some sem aviso).
    if (touched.has(n.id)) {
      branchesOf(d).forEach(b => {
        const linked = edges.some(
          e => e.source === n.id && (e.sourceHandle ?? null) === b.id
        );
        if (!linked) {
          issues.push({
            level: 'warn',
            key: 'BRANCH_DANGLING',
            params: { label, branch: b.label },
          });
        }
      });
    }
    // Nenhuma checagem de "caixa obrigatória" para flexcalls_call: o modo
    // "both" (ligar e receber) é Fase 2 e fica desabilitado no inspector —
    // bloquear o save por falta de caixa aqui puniria uma configuração que o
    // operador nem consegue mais escolher, sem um jeito de resolver.
  });

  return issues;
}
